import type { OrgTipo } from './navigation';

const KEY = 'toscana.clinicPreview';

export type ClinicPreviewOrg =
  | 'clinica-medica'
  | 'clinica-odontologica'
  | 'clinica-veterinaria'
  | 'clinica-nutricion'
  | 'clinica-psicologia'
  | 'clinica-estetica'
  | 'clinica-optica'
  | 'clinica-fisioterapia';

/** Tenant demo en VM (1 por rubro clínico). */
export const DEMO_TENANT_BY_ORG: Record<ClinicPreviewOrg, string> = {
  'clinica-medica': 'clinica-demo-medica',
  'clinica-odontologica': 'clinica-demo-odonto',
  'clinica-veterinaria': 'clinica-demo-vet',
  'clinica-nutricion': 'clinica-demo-nutricion',
  'clinica-psicologia': 'clinica-demo-psico',
  'clinica-estetica': 'clinica-demo-estetica',
  'clinica-optica': 'clinica-demo-optica',
  'clinica-fisioterapia': 'clinica-demo-fisio',
};

export function demoTenantForOrg(org: ClinicPreviewOrg | string | null | undefined): string {
  if (org && org in DEMO_TENANT_BY_ORG) return DEMO_TENANT_BY_ORG[org as ClinicPreviewOrg];
  return DEMO_TENANT_BY_ORG['clinica-medica'];
}

export function demoAdminUser(tenantId: string): string {
  return `${tenantId}@toscanasystems.com`;
}

export function isDemoTenant(tenantId: string | null | undefined): boolean {
  if (!tenantId) return false;
  if (tenantId === 'clinica-solo-medico') return true;
  return tenantId.startsWith('clinica-demo-');
}

/** Tenant demo → rubro (para fallback de contexto). */
export const DEMO_TENANT_TO_ORG: Record<string, ClinicPreviewOrg> = Object.fromEntries(
  Object.entries(DEMO_TENANT_BY_ORG).map(([org, tid]) => [tid, org as ClinicPreviewOrg]),
) as Record<string, ClinicPreviewOrg>;

export function orgFromDemoTenant(tenantId: string | null | undefined): ClinicPreviewOrg | null {
  if (!tenantId) return null;
  return DEMO_TENANT_TO_ORG[tenantId] ?? null;
}

/** Credenciales piloto según rubro clínico en sessionStorage (evita cruce con hosp-demo). */
export function resolveDemoPilotCredentials(defaults: {
  tenantId: string;
  usuario: string;
}): { tenantId: string; usuario: string } {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (raw && raw in DEMO_TENANT_BY_ORG) {
      const org = raw as ClinicPreviewOrg;
      const tenantId = DEMO_TENANT_BY_ORG[org];
      return { tenantId, usuario: demoAdminUser(tenantId) };
    }
  } catch { /* ignore */ }
  return defaults;
}

export const CLINIC_ORG_OPTIONS: { value: ClinicPreviewOrg; label: string; emoji: string }[] = [
  { value: 'clinica-medica', label: 'Medicina general', emoji: '' },
  { value: 'clinica-odontologica', label: 'Odontología', emoji: '' },
  { value: 'clinica-veterinaria', label: 'Veterinaria', emoji: '' },
  { value: 'clinica-nutricion', label: 'Nutrición', emoji: '' },
  { value: 'clinica-psicologia', label: 'Psicología', emoji: '' },
  { value: 'clinica-estetica', label: 'Estética', emoji: '' },
  { value: 'clinica-optica', label: 'Óptica', emoji: '' },
  { value: 'clinica-fisioterapia', label: 'Fisioterapia', emoji: '' },
];

const SLUG_TO_ORG: Record<string, ClinicPreviewOrg> = {
  medica: 'clinica-medica',
  odonto: 'clinica-odontologica',
  odontologica: 'clinica-odontologica',
  vet: 'clinica-veterinaria',
  veterinaria: 'clinica-veterinaria',
  nutricion: 'clinica-nutricion',
  psico: 'clinica-psicologia',
  psicologia: 'clinica-psicologia',
  estetica: 'clinica-estetica',
  optica: 'clinica-optica',
  fisio: 'clinica-fisioterapia',
  fisioterapia: 'clinica-fisioterapia',
};

export function orgFromDemoSlug(slug: string | null | undefined): ClinicPreviewOrg | null {
  if (!slug) return null;
  return SLUG_TO_ORG[slug.toLowerCase()] ?? null;
}

function load(): ClinicPreviewOrg | null {
  try {
    const v = sessionStorage.getItem(KEY);
    if (v) return v as ClinicPreviewOrg;
  } catch { /* ignore */ }
  return null;
}

let _preview = $state<ClinicPreviewOrg | null>(load());

export const clinicPreview = {
  get org(): ClinicPreviewOrg | null { return _preview; },
  set org(v: ClinicPreviewOrg | null) {
    _preview = v;
    try {
      if (v) sessionStorage.setItem(KEY, v);
      else sessionStorage.removeItem(KEY);
    } catch { /* ignore */ }
  },
};

/** Módulos licenciados al previsualizar un rubro clínico en demo/piloto (clínica privada, sin hospital). */
export function previewModulesFor(org: ClinicPreviewOrg): string[] {
  const plus = [
    'citas', 'admision', 'expediente', 'facturacion',
    'laboratorio', 'imagenologia', 'bodega', 'tesoreria', 'aseguradoras',
  ];
  switch (org) {
    case 'clinica-veterinaria':
      return [...plus, 'veterinaria'];
    case 'clinica-optica':
      return [...plus, 'optica'];
    default:
      return plus;
  }
}

/** Org efectivo: preview manual (demo) o config del tenant. */
export function effectiveOrgTipo(
  tenantOrg: OrgTipo | null,
  isHospital: boolean,
  tenantId?: string | null,
): OrgTipo | null {
  if (clinicPreview.org) return clinicPreview.org;
  if (isHospital) return tenantOrg;
  return tenantOrg ?? orgFromDemoTenant(tenantId ?? null);
}
